import React, { useCallback } from "react";
import { useFragment, useLazyLoadQuery } from "react-relay";
import type { ActionMeta, OnChangeValue } from "react-select";

import { faHashtag, faLock } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { graphql } from "relay-runtime";

import { showFeatureModal, UpgradeIcon } from "~/billing";
import { useID } from "~/lib/hooks";
import { useMutationPromise } from "~/lib/relayUtil";
import { getFirstUserFacingError } from "~/lib/utils";
import ContactChannelSelector, { type ContactChannelOption } from "~/meetingRecorder/ContactChannelSelector";
import { toast } from "~/ui";
import { ButtonLink, Switch } from "~/ui/legacyFellowIndex";

import { Header } from "./Tasks";
import type { AutomationType } from "./types";

import type { AutoShareToChannelsAddChannelMutation } from "~/__generated__/AutoShareToChannelsAddChannelMutation.graphql";
import type { AutoShareToChannelsRemoveChannelMutation } from "~/__generated__/AutoShareToChannelsRemoveChannelMutation.graphql";
import type { AutoShareToChannelsSetEnabledMutation } from "~/__generated__/AutoShareToChannelsSetEnabledMutation.graphql";
import type { AutoShareToChannelsSettings_stream$key } from "~/__generated__/AutoShareToChannelsSettings_stream.graphql";
import type { AutoShareToChannelsSettingsQuery } from "~/__generated__/AutoShareToChannelsSettingsQuery.graphql";

import settingsStyles from "~/settings/css/settings.module.css";

import automationStyles from "./Automations.module.css";
import styles from "./AutoShareToChannels.module.css";

export const AUTO_SHARE_TO_CHANNELS_AUTOMATION: AutomationType = {
	id: "fake",
	name: "Share to Fellow channels",
	codeName: "auto-share-to-channels",
	description: "Automatically add Meeting Recaps and Notes in this series to selected Fellow channels.",
};

function useAddChannelMutation() {
	const [commit, isInFlight] = useMutationPromise<AutoShareToChannelsAddChannelMutation>(graphql`
		mutation AutoShareToChannelsAddChannelMutation($streamId: ID!, $channelId: ID!) {
			streamAutoShareSettingsAddChannel(streamId: $streamId, channelId: $channelId) {
				streamAutoShareSettings {
					channels {
						id
						name
						isPrivate
					}
				}
			}
		}
	`);

	const addChannel = React.useCallback(
		async (streamId: string, channelId: string) => {
			try {
				await commit({ variables: { streamId, channelId } });
			} catch (errors) {
				const userFacingError = getFirstUserFacingError(errors) ?? "Something went wrong, please try again.";
				toast.error(userFacingError, { title: "Failed to save settings" });
			}
		},
		[commit],
	);

	return [addChannel, isInFlight] as const;
}

function useRemoveChannelMutation() {
	const [commit, isInFlight] = useMutationPromise<AutoShareToChannelsRemoveChannelMutation>(graphql`
		mutation AutoShareToChannelsRemoveChannelMutation($streamId: ID!, $channelId: ID!) {
			streamAutoShareSettingsRemoveChannel(streamId: $streamId, channelId: $channelId) {
				streamAutoShareSettings {
					channels {
						id
						name
						isPrivate
					}
				}
			}
		}
	`);

	const removeChannel = React.useCallback(
		async (streamId: string, channelId: string) => {
			try {
				await commit({ variables: { streamId, channelId } });
			} catch (errors) {
				const userFacingError = getFirstUserFacingError(errors) ?? "Something went wrong, please try again.";
				toast.error(userFacingError, { title: "Failed to save settings" });
			}
		},
		[commit],
	);

	return [removeChannel, isInFlight] as const;
}

function useSetEnabledMutation() {
	const [commit, isInFlight] = useMutationPromise<AutoShareToChannelsSetEnabledMutation>(graphql`
		mutation AutoShareToChannelsSetEnabledMutation($streamId: ID!, $value: Boolean!) @raw_response_type {
			streamAutoShareSettingsSetEnabled(streamId: $streamId, value: $value) {
				streamAutoShareSettings {
					id
					enabled
				}
			}
		}
	`);

	const setEnabled = React.useCallback(
		async (streamId: string, settingsId: string, value: boolean) => {
			try {
				await commit({
					variables: { streamId, value },
					optimisticResponse: {
						streamAutoShareSettingsSetEnabled: {
							streamAutoShareSettings: { id: settingsId, enabled: value },
						},
					},
				});
			} catch (errors) {
				const userFacingError = getFirstUserFacingError(errors) ?? "Something went wrong, please try again.";
				toast.error(userFacingError, { title: "Failed to save settings" });
			}
		},
		[commit],
	);

	return [setEnabled, isInFlight] as const;
}

const streamSettingsFragment = graphql`
	fragment AutoShareToChannelsSettings_stream on NoteStream {
		autoShareSettings {
			id
			enabled
			channels {
				id
				name
				isPrivate
			}
		}
	}
`;

type ConfigurationPageProps = ConfigurationPageRendererProps & {
	stream: AutoShareToChannelsSettings_stream$key;
};

function ConfigurationPage({ streamId, onBack, stream: streamKey }: ConfigurationPageProps) {
	const { autoShareSettings } = useFragment(streamSettingsFragment, streamKey);

	const initialChannels = autoShareSettings.channels.map(channel => ({
		label: channel.name,
		value: channel.id,
		icon: channel.isPrivate ? faLock : faHashtag,
	}));

	const [selectedChannels, setSelectedChannels] = React.useState<readonly ContactChannelOption[]>(initialChannels);
	const [addChannel, addMutationInProgress] = useAddChannelMutation();
	const [removeChannel, removeMutationInProgress] = useRemoveChannelMutation();

	const selectNewChannel = useCallback(
		(channel: { id: string; name: string; isPrivate: boolean }) => {
			addChannel(streamId, channel.id).then(() => {
				setSelectedChannels(prevSelections => [
					...prevSelections,
					{
						label: channel.name,
						value: channel.id,
						icon: channel.isPrivate ? faLock : faHashtag,
					},
				]);
			});
		},
		[addChannel, streamId],
	);

	const onChannelsChange = React.useCallback(
		(value: OnChangeValue<ContactChannelOption, boolean>, meta: ActionMeta<ContactChannelOption>) => {
			if (addMutationInProgress || removeMutationInProgress) return;

			if (value) {
				if (value instanceof Array) {
					setSelectedChannels(value);
				} else {
					setSelectedChannels([value]);
				}
			} else {
				setSelectedChannels([]);
			}

			if (meta.action === "select-option" && meta.option) {
				const channelId = meta.option.value;
				addChannel(streamId, channelId);
			} else if (meta.action === "remove-value" || meta.action === "pop-value") {
				const channelId = meta.removedValue.value;
				removeChannel(streamId, channelId);
			}
		},
		[streamId, addChannel, addMutationInProgress, removeChannel, removeMutationInProgress],
	);

	return (
		<>
			<Header name={AUTO_SHARE_TO_CHANNELS_AUTOMATION.name} onHome={onBack} onBack={onBack} />

			<div className={styles.box}>
				<label className={styles.label}>
					After a meeting, automatically share the Meeting Note and Recap (if available) to the following Fellow
					channels:
				</label>

				<ContactChannelSelector
					placeholder="Select Fellow channels"
					value={selectedChannels}
					onChange={onChannelsChange}
					afterNewChannelCreated={selectNewChannel}
					defaultOptions
					isMulti
					skipContacts
					autoFocus
					userIdsToExclude={[]}
				/>
			</div>
		</>
	);
}

type AutomationProps = AutomationRendererProps & {
	stream: AutoShareToChannelsSettings_stream$key;
};

function Automation({ isPaywalled, streamId, onConfigure, stream: streamKey }: AutomationProps) {
	const { autoShareSettings } = useFragment(streamSettingsFragment, streamKey);

	const toggleID = useID();
	const configured = autoShareSettings.channels.length > 0;
	const [setEnabled, mutationInProgress] = useSetEnabledMutation();

	const showPaywall = React.useCallback(() => showFeatureModal("meeting_automations"), []);

	const onChange = React.useCallback(
		(checked: boolean) => {
			if (mutationInProgress) return;

			if (isPaywalled) {
				showPaywall();
			} else {
				setEnabled(streamId, autoShareSettings.id, checked);

				if (checked && !configured) {
					// If the user enables the automation without configuring it, we open the configuration page
					onConfigure();
				}
			}
		},
		[isPaywalled, mutationInProgress, streamId, autoShareSettings.id, showPaywall, setEnabled, configured, onConfigure],
	);

	return (
		<section className={automationStyles.automationBlock}>
			<div className={automationStyles.row}>
				<h1 className={automationStyles.title}>{AUTO_SHARE_TO_CHANNELS_AUTOMATION.name}</h1>
				<ButtonLink onClick={isPaywalled ? showPaywall : onConfigure} className={automationStyles.configureLink}>
					Configure
				</ButtonLink>
			</div>
			<div className={automationStyles.row}>
				<label htmlFor={toggleID} className={automationStyles.label}>
					{AUTO_SHARE_TO_CHANNELS_AUTOMATION.description}
				</label>
				{isPaywalled && <UpgradeIcon />}
				<Switch
					id={toggleID}
					checked={isPaywalled ? false : autoShareSettings.enabled}
					onCheckedChange={onChange}
					className={settingsStyles.settingsPageToggle}
					data-disabled={isPaywalled ?? undefined}
				/>
			</div>
		</section>
	);
}

const settingsQuery = graphql`
	query AutoShareToChannelsSettingsQuery($streamId: ID!) {
		stream: node(id: $streamId) {
			... on NoteStream {
				...AutoShareToChannelsSettings_stream
			}
		}
	}
`;

interface ConfigurationPageRendererProps {
	streamId: string;
	onBack: () => void;
}

export function ConfigurationPageRenderer(props: ConfigurationPageRendererProps) {
	const { stream } = useLazyLoadQuery<AutoShareToChannelsSettingsQuery>(settingsQuery, { streamId: props.streamId });

	if (!stream) {
		return null;
	}

	return <ConfigurationPage {...props} stream={stream} />;
}

interface AutomationRendererProps {
	isPaywalled: boolean;
	streamId: string;
	onConfigure: () => void;
}

function AutomationRenderer(props: AutomationRendererProps) {
	const { stream } = useLazyLoadQuery<AutoShareToChannelsSettingsQuery>(
		settingsQuery,
		{ streamId: props.streamId },
		{ fetchPolicy: "store-and-network" },
	);

	if (!stream) {
		return null;
	}

	return <Automation {...props} stream={stream} />;
}

export default AutomationRenderer;
